import {
	createFileRoute,
	RouterProvider,
	useRouter,
} from "@tanstack/react-router";
import { renderToReadableStream } from "react-dom/server";
import { BlockPreviewContext } from "#/_packages/umbrella/features/previews/block-preview-context";
import { preloadRendererModules } from "#/_packages/umbrella/integrations/umbraco/utils/renderer-modules";
import type {
	ContentFeedItemDto,
	IApiElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { getContentFeedContentByIdFilter } from "#/_packages/umbrella/integrations/umbraco/utils/block-utils.ts";
import { BlockGridItemRenderer } from "#/_packages/umbrella/integrations/umbraco/utils/blockgrid-item-renderer.tsx";
import { ElementTypes } from "#/_packages/umbrella/integrations/umbraco/utils/element-types.ts";
import { UmbracoRendererProvider } from "#/_packages/umbrella/integrations/umbraco/utils/renderer-mappings.tsx";
import { getContentFeedContentById } from "#/_packages/umbrella/integrations/umbraco/utils/server-functions.ts";
import { gsaBlockGridItemRenderers } from "#/app/features/rendering/components/blocktype-renderer.tsx";
import { getPreviewRouter } from "#/app/router.tsx";
import { m } from "#/paraglide/messages";

export type UmbracoBlockPreviewBlock = {
	content: IApiElementModel;
	settings?: IApiElementModel;
};

export const Route = createFileRoute(
	"/_umbraco/_preview/umbraco/block-preview",
)({
	server: {
		handlers: {
			POST: async ({ request }) => {
				const block = await request.json();

				const router = getPreviewRouter();
				router.update({
					context: {
						...router.options.context,
						previewBlock: block,
					},
				});
				await router.load();
				const html = await renderToReadableStream(
					<RouterProvider router={router} />,
				);
				await html.allReady;
				return new Response(html, {
					headers: {
						"Content-Type": "text/html; charset=utf-8",
					},
				});
			},
		},
	},
	loader: async ({ context }) => {
		const block = context.previewBlock as UmbracoBlockPreviewBlock;
		const loaderData: { data: null | ContentFeedItemDto[] } = { data: null };
		if (!block) return loaderData;
		await preloadRendererModules(block);
		switch (block.content.contentType) {
			case ElementTypes.ContentFeed: {
				const contentFeedFilter = getContentFeedContentByIdFilter(
					block.content?.properties,
				);

				const res = await getContentFeedContentById({
					data: { id: block.content.id, params: contentFeedFilter },
				});

				loaderData.data = res.data;
				return loaderData;
			}
		}

		return loaderData;
	},

	component: () => {
		const router = useRouter();

		if (!router.options.context.previewBlock)
			return <>{m.previews_block_unavailable()}</>;

		return (
			<BlockPreviewContext value={true}>
				<UmbracoRendererProvider blocks={gsaBlockGridItemRenderers}>
					<BlockGridItemRenderer
						model={router.options.context.previewBlock}
						options={{ isPreview: true }}
					/>
				</UmbracoRendererProvider>
			</BlockPreviewContext>
		);
	},
});
